import "~/misc/common";

import {
  Chart, ChartOptions, Plugin, ArcElement, PointElement, LineElement, BarElement,
  Filler, Tooltip, Legend, Title,
  LinearScale, LogarithmicScale, CategoryScale, TimeScale,
  LineController, ScatterController, DoughnutController, BarController,
  ChartData,
  TimeScaleTimeOptions,
} from "chart.js";
Chart.register(ArcElement, PointElement, LineElement, BarElement,
  Filler, Tooltip, Legend, Title,
  LinearScale, LogarithmicScale, CategoryScale, TimeScale,
  LineController, ScatterController, DoughnutController, BarController);
import zoomPlugin from "chartjs-plugin-zoom";
import annotationPlugin from "chartjs-plugin-annotation";
import 'chartjs-adapter-moment';
import $ from "jquery";

// ✅ Register zoom plugin globally
Chart.register(zoomPlugin);
Chart.register(annotationPlugin);

// === Default color palette ===
export const defaultChartColors: string[] = [
  "#DC3912", "#3366cc", "#ff9900", "#109618", "#990099", "#3B3EAC",
  "#0099C6", "#DD4477", "#66AA00", "#B82E2E", "#316395", "#994499",
  "#22AA99", "#AAAA11", "#6633CC", "#E67300", "#8B0707", "#329262",
  "#5574A6", "#3B3EAC",
];

// === Utility ===
export function roundToHUF(value: number): string {
  return Math.round(value)
    .toFixed(1)
    .replace(/\d(?=(\d{3})+\.)/g, "$& ")
    .replace(".0", "");
}

// === Common chart options ===
export const lineChartOptions: ChartOptions<"line"> = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      labels: { usePointStyle: true },
    },
  },
  scales: {
    x: { title: { display: false } },
    y: { title: { display: true } },
  },
};

export const xTimeScaleOptions: Partial<TimeScaleTimeOptions> = {
  unit: 'hour',
  tooltipFormat: 'MM.DD HH:mm',
  displayFormats: {
    minute: 'HH:mm',
    hour: 'MM.DD HH:mm',
    day: 'MM.DD'
  }
};

export function cloneScatterChartOptions(overrides: Partial<ChartOptions<'scatter'>> = {}): ChartOptions<'scatter'> {
  return {
    ...structuredClone(lineChartOptions) as ChartOptions<'scatter'>,
    ...overrides,
  };
}

export const scatterChartOptions: ChartOptions<"scatter"> = {
  elements: {
    line: {
      borderWidth: 1.5
    },
    point: {
      radius: 2
    }
  },
  scales: {
    x: {
      title: {
        display: true
      }
    },
    y: {
      title: {
        display: true
      }
    }
  },
  plugins: {
    tooltip: {
      callbacks: {
        label: function (context) {
          return (context.raw as any).label;
        }
      }
    },
    legend: {
      display: true,
      labels: { usePointStyle: true }
    }
  },
  responsive: true,
  maintainAspectRatio: false,
};

export type ScatterData = ChartData<'scatter', { x: number, y: number; }[]>['datasets'];

export const barChartOptions: ChartOptions<"bar"> = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      labels: { usePointStyle: true },
    },
  },
  scales: {
    x: {
      stacked: false,
      title: { display: false, text: "Hónap" },
      ticks: { autoSkip: false },
    },
    y: { title: { display: true } },
  },
};

export const doughnutChartOptions: ChartOptions<"doughnut"> = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    tooltip: {
      callbacks: {
        label(context) {
          const label = context.label || "";
          const value = context.parsed as number;
          return `${label}: ${roundToHUF(value)}`;
        },
      },
    },
  },
};

// === Zoom options (for chartjs-plugin-zoom v1.x) ===
export const zoomOptions = {
  pan: {
    enabled: true,
    mode: "xy" as const,
  },
  zoom: {
    wheel: { enabled: true },
    pinch: { enabled: true },
    mode: "xy" as const,
  },
};

// === Padding plugin (adds legend spacing) ===
export function paddingPlugin(padding: number): Plugin {
  return {
    id: "paddingPlugin",
    beforeInit(chart) {
      const originalFit = (chart.legend as any).fit;
      (chart.legend as any).fit = function fit() {
        originalFit.bind(chart.legend)();
        this.height += padding;
      };
    },
  };
}

// === Sum at top for stacked bars ===
export const sumAtColumnTop = {
  anchor: "end" as const,
  align: "top" as const,
  formatter(value: number, context: any): string {
    const datasetArray: number[] = [];
    context.chart.data.datasets.forEach((dataset: any) => {
      if (dataset.data[context.dataIndex] != null) {
        datasetArray.push(dataset.data[context.dataIndex]);
      }
    });

    const sum = datasetArray.reduce((total, d) => total + d, 0);
    return context.datasetIndex === datasetArray.length - 1
      ? roundToHUF(sum)
      : "";
  },
};

// === Double-click reset zoom ===
$(document).on("ready", function () {
  $("canvas.chart-dblclick-reset-zoom").on("dblclick", function (e) {
    if (e.button === 0) {
      const chart = Chart.getChart(e.target as HTMLCanvasElement);
      if (chart && (chart as any).resetZoom) {
        (chart as any).resetZoom();
      }
    }
  });
});
